import { onSnapshot, orderBy, query, where } from "firebase/firestore";
import type { CollectionReference, DocumentData, FirestoreError, Unsubscribe } from "firebase/firestore";

type BatchDocument = { firebaseId: string };

/** Load recent and future operations, including records awaiting a date. */
export function subscribeRecentBatchesByDate<T extends BatchDocument>(
  collectionRef: CollectionReference<DocumentData>, dateField: "plantDate" | "freezeDryStart", cutoff: number,
  onNext: (batches: T[]) => void, onError: (error: FirestoreError) => void,
): Unsubscribe {
  const snapshots: Array<T[] | undefined> = [undefined, undefined];
  const unsubscribers: Unsubscribe[] = [];
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true; unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
  const queries = [
    query(collectionRef, where(dateField, ">=", cutoff), orderBy(dateField, "desc")),
    query(collectionRef, where(dateField, "==", null)),
  ];
  queries.forEach((batchQuery, index) => {
    if (stopped) return;
    const unsubscribe = onSnapshot(batchQuery, (snapshot) => {
      if (stopped) return;
      const batches: T[] = [];
      snapshot.forEach((entry) => batches.push({ ...entry.data(), firebaseId: entry.id } as T));
      snapshots[index] = batches;
      if (snapshots.every((items) => items !== undefined)) {
        const unique = new Map(snapshots.flatMap((items) => items!).map((batch) => [batch.firebaseId, batch]));
        onNext(Array.from(unique.values()));
      }
    }, (error) => {
      if (stopped) return;
      stop(); onError(error);
    });
    // A synchronous subscription failure must also clean up its own listener.
    if (stopped) unsubscribe();
    else unsubscribers.push(unsubscribe);
  });
  return stop;
}

/** Live dates replace stale copies, while previously loaded older dates remain. */
export function mergeBatchDateWindow<T extends BatchDocument>(
  existing: T[], live: T[], dateField: keyof T, cutoff: number, belongsToGroup: (batch: T) => boolean,
): T[] {
  const liveIds = new Set(live.map((batch) => batch.firebaseId));
  return [
    ...live.filter(belongsToGroup),
    ...existing.filter((batch) => {
      const date = batch[dateField];
      return typeof date === "number" && date < cutoff && !liveIds.has(batch.firebaseId);
    }),
  ];
}
